import React from 'react';
import { Mail, MapPin, CreditCard, Clock, FileText, Award, Check, ChevronRight } from 'lucide-react';
import { Button } from "@mantis-sas/ui";
import { RatingStars } from './RatingStars';
import { StatusBadge } from './StatusBadge';

export const SupplierCard = ({ 
  supplier, 
  hasQuotationItems, 
  isSelected, 
  onSelect, 
  onViewDocuments,
  className = "" 
}) => {
  // Determinar el estado de respuesta
  const responseStatus = hasQuotationItems ? "received" : "pending_response";
  
  // Calcular rating promedio
  const ratings = [
    supplier.rating_service,
    supplier.rating_punctuality,
    supplier.rating_quality,
    supplier.rating_overall,
  ].filter((rating) => {
    const numRating = Number(rating);
    return (
      rating !== null &&
      rating !== undefined &&
      !isNaN(numRating) &&
      numRating > 0
    );
  });

  const avgRating =
    ratings.length > 0
      ? ratings.reduce((sum, rating) => sum + Number(rating), 0) /
        ratings.length
      : 0;

  return (
    <div
      className={`
        group relative overflow-hidden bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm
        border border-slate-200/50 dark:border-slate-700/50 rounded shadow-md hover:shadow-lg
        transition-all duration-200 cursor-pointer
        ${
          isSelected
            ? "ring-2 ring-blue-500 dark:ring-blue-400 border-blue-200 dark:border-blue-600 bg-blue-50/30 dark:bg-blue-900/20"
            : "hover:border-slate-300 dark:hover:border-slate-600"
        }
        ${
          hasQuotationItems
            ? "hover:shadow-green-100/50 dark:hover:shadow-green-900/20"
            : ""
        }
        ${className}
      `}
      onClick={() => onSelect?.(supplier.id)}
    >
      {/* Glassmorphism gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent dark:from-slate-700/20 pointer-events-none" />

      {/* Selection indicator */}
      {isSelected && (
        <div className="absolute top-0 right-0 w-0 h-0 border-l-[16px] border-l-transparent border-t-[16px] border-t-blue-500">
          <Check className="absolute -top-3.5 -right-3 h-2.5 w-2.5 text-white transform rotate-45" />
        </div>
      )}

      {/* Main Content */}
      <div className="p-3">
        {/* Header with Name and Status */}
        <div className="flex items-start justify-between mb-2">
          <div className="flex items-center space-x-2 flex-1 min-w-0">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold text-xs flex-shrink-0">
              {supplier.name?.charAt(0)?.toUpperCase() || "S"}
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="font-medium text-slate-800 dark:text-slate-200 text-xs truncate">
                {supplier.name}
              </h3>
              <div className="flex items-center space-x-1 mt-0.5">
                <StatusBadge
                  status={responseStatus}
                  type="response"
                  className="text-xs px-1.5 py-0.5"
                />
                {supplier.is_favorite && <span className="text-xs">⭐</span>}
              </div>
            </div>
          </div>

          {/* Language badge */}
          {supplier.language && (
            <StatusBadge
              status={supplier.language}
              type="language"
              className="text-xs px-1.5 py-0.5"
            />
          )}
        </div>

        {/* Rating and Currency */}
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center space-x-1">
            <RatingStars rating={avgRating} size="xs" showValue={false} />
            <span className="text-xs text-slate-600 dark:text-slate-400">
              {isNaN(avgRating) ? "0.0" : avgRating.toFixed(1)}
            </span>
          </div>
          <div className="text-xs text-slate-500 dark:text-slate-400 font-medium">
            {supplier.currency || "USD"}
          </div>
        </div>

        {/* Contact Information - Compact */}
        <div className="space-y-1 mb-2">
          <div className="flex items-center text-xs text-slate-600 dark:text-slate-400">
            <Mail className="h-3 w-3 mr-1.5 flex-shrink-0 text-blue-500" />
            <span className="truncate">
              {supplier.contact_email || supplier.email || "No email"}
            </span>
          </div>

          <div className="flex items-center justify-between text-xs text-slate-600 dark:text-slate-400">
            <div className="flex items-center">
              <MapPin className="h-3 w-3 mr-1.5 flex-shrink-0 text-red-500" />
              <span className="truncate">{supplier.country || "N/A"}</span>
            </div>

            <div className="flex items-center">
              <CreditCard className="h-3 w-3 mr-1.5 flex-shrink-0 text-purple-500" />
              <span className="truncate">{supplier.nit || "N/A"}</span>
            </div>
          </div>
        </div>

        {/* Capabilities - Compact */}
        {supplier.capabilities && supplier.capabilities.length > 0 && (
          <div className="mb-2">
            <div className="flex items-center mb-1">
              <Award className="h-3 w-3 mr-1 text-amber-500" />
              <span className="text-xs font-medium text-slate-700 dark:text-slate-300">
                Skills
              </span>
            </div>
            <div className="flex flex-wrap gap-1">
              {supplier.capabilities.slice(0, 2).map((capability, index) => (
                <span
                  key={index}
                  className="px-1.5 py-0.5 bg-slate-100 dark:bg-slate-700 text-xs rounded text-slate-600 dark:text-slate-400"
                >
                  {capability}
                </span>
              ))}
              {supplier.capabilities.length > 2 && (
                <span className="px-1.5 py-0.5 bg-slate-100 dark:bg-slate-700 text-xs rounded text-slate-600 dark:text-slate-400">
                  +{supplier.capabilities.length - 2}
                </span>
              )}
            </div>
          </div>
        )}

        {/* Lead Time - If available */}
        {supplier.typicalLeadTime && (
          <div className="flex items-center text-xs text-slate-600 dark:text-slate-400 mb-2">
            <Clock className="h-3 w-3 mr-1.5 flex-shrink-0 text-orange-500" />
            <span>{supplier.typicalLeadTime}</span>
          </div>
        )}

        {/* Action Buttons - Compact */}
        <div className="flex justify-between items-center gap-2">
          {hasQuotationItems && (
            <Button
              variant="ghost"
              size="sm"
              className="text-xs h-6 px-2 text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
              onClick={(e) => {
                e.stopPropagation();
                onViewDocuments?.(supplier.id);
              }}
            >
              <FileText className="h-3 w-3 mr-1" />
              Docs
            </Button>
          )}

          <Button
            variant={isSelected ? "default" : "outline"}
            size="sm"
            className={`
              text-xs h-6 px-2 transition-all flex-1 max-w-[80px]
              ${
                isSelected
                  ? "bg-blue-600 hover:bg-blue-700 text-white"
                  : hasQuotationItems
                  ? "text-green-600 border-green-300 hover:bg-green-50 hover:border-green-400 dark:text-green-400 dark:border-green-700 dark:hover:bg-green-900/20"
                  : "text-slate-600 border-slate-300 hover:bg-slate-50 dark:text-slate-400 dark:border-slate-600 dark:hover:bg-slate-800"
              }
            `}
            onClick={(e) => {
              e.stopPropagation();
              onSelect?.(isSelected ? null : supplier.id);
            }}
            disabled={!hasQuotationItems}
          >
            {isSelected ? (
              <>
                <Check className="h-3 w-3 mr-1" />
                Selected
              </>
            ) : hasQuotationItems ? (
              <>
                <ChevronRight className="h-3 w-3 mr-1" />
                Select
              </>
            ) : (
              "Pending"
            )}
          </Button>
        </div>
      </div>

      {/* Hover effect */}
      <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 to-purple-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none" />
    </div>
  );
}; 